import React, { useEffect, useMemo, useState } from "react";
import { X, Sparkles, AlertTriangle, Users, Ban, Plus, Download, Check, TriangleAlert, Minus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import RestrictionsEditor from "@/components/event/RestrictionsEditor";

/**
 * Modale d'auto-assignation des arbitres (referee1 + referee2) selon des
 * contraintes configurables. Mode preview d'abord, puis application avec
 * snapshot automatique.
 */
export default function AutoAssignModal({ eventId, eventName, officials, hiddenOfficialIds = [], games = [], surfaces: eventSurfaces = 0, initialBlockedCategories = {}, initialForbiddenPairs = [], initialExcludedIds = [], initialIsolatedSurfaces = [], initialRestrictedSurfaces = {}, onClose, onApplied }) {
  const [availableIds, setAvailableIds] = useState(null); // null = loading, Set = loaded
  const [consecutiveMax, setConsecutiveMax] = useState(3);
  const [maxPerDay, setMaxPerDay] = useState("");
  const [maxBreakGames, setMaxBreakGames] = useState("");
  const [preserveManual, setPreserveManual] = useState(true);
  // Contraintes HARD : taille de bloc et pause (Feb 2026). Valeurs vides =
  // désactivé (compat historique).
  const [preferredBlockMin, setPreferredBlockMin] = useState("");
  const [preferredBlockMax, setPreferredBlockMax] = useState("");
  const [preferredPauseMin, setPreferredPauseMin] = useState("");
  // Surfaces marquées éloignées — chargées depuis l'événement, persistées via PATCH.
  const [isolatedSurfaces, setIsolatedSurfaces] = useState(() => new Set((initialIsolatedSurfaces || []).map(String)));
  // Restrictions HARD surfaces par arbitre × jour :
  //   { oid: { "YYYY-MM-DD": Set<string_surface> } }
  const [restrictedByOff, setRestrictedByOff] = useState(() => {
    const out = {};
    for (const [oid, perDate] of Object.entries(initialRestrictedSurfaces || {})) {
      const by = {};
      for (const [d, surfs] of Object.entries(perDate || {})) {
        by[d] = new Set((surfs || []).map(String));
      }
      if (Object.keys(by).length > 0) out[oid] = by;
    }
    return out;
  });
  const [floatingIds, setFloatingIds] = useState(new Set());
  // Arbitres explicitement retirés de l'auto-assignation (chargés depuis l'événement).
  const [excludedIds, setExcludedIds] = useState(() => new Set(initialExcludedIds || []));
  // Paires d'arbitres à ne jamais jumeler sur une même partie.
  // Format : [[oid_a, oid_b], ...] — les paires sont non ordonnées côté backend.
  // Initialisées depuis l'événement pour persister d'un run à l'autre.
  const [forbiddenPairs, setForbiddenPairs] = useState(() => initialForbiddenPairs || []);
  const [pickA, setPickA] = useState("");
  const [pickB, setPickB] = useState("");
  // Catégories bloquées par arbitre pour CE tournoi.
  // Structure : { [official_id]: Set<category> } — chargée depuis l'événement.
  const [blockedByOfficial, setBlockedByOfficial] = useState(() => {
    const out = {};
    for (const [oid, cats] of Object.entries(initialBlockedCategories || {})) {
      out[oid] = new Set(cats || []);
    }
    return out;
  });
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);

  // Liste des catégories réellement présentes dans l'horaire du tournoi.
  // S'ajuste automatiquement à chaque tournoi grâce à la prop `games`.
  const eventCategories = useMemo(() => {
    const s = new Set();
    for (const g of games || []) {
      if (g.is_break) continue;
      const c = (g.category || "").trim();
      if (c) s.add(c);
    }
    return [...s].sort();
  }, [games]);

  // Fetch declared availabilities on mount to filter the floating list
  // to referees who actually have at least one "yes" answer for this event.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/events/${eventId}/availabilities`);
        if (cancelled) return;
        const ids = new Set();
        for (const a of (data.answers || [])) {
          const answers = a.answers || {};
          let hasYes = false;
          for (const v of Object.values(answers)) {
            if (v === "yes") { hasYes = true; break; }
            if (v && typeof v === "object") {
              for (const sv of Object.values(v)) {
                if (sv === "yes") { hasYes = true; break; }
              }
              if (hasYes) break;
            }
          }
          if (hasYes && a.official_id) ids.add(a.official_id);
        }
        setAvailableIds(ids);
      } catch (e) {
        // Non-blocking : si l'endpoint échoue, on retombe en mode "tous les arbitres"
        setAvailableIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [eventId]);

  // Arbitres candidats aux volants : uniquement ceux qui ont déclaré au moins
  // un créneau "OUI" pour cet événement. Si aucune dispo n'a été déclarée
  // (mode permissif), on affiche tous les arbitres actifs.
  // Officiels masqués pour cet événement : on les retire complètement des
  // pickers (blocked categories, forbidden pairs, floating).
  const hiddenSet = useMemo(() => new Set(hiddenOfficialIds || []), [hiddenOfficialIds]);

  const referees = useMemo(() => {
    const activeReferees = officials.filter(
      (o) => (o.roles || []).includes("referee") && o.status !== "pending" && o.status !== "blocked" && !hiddenSet.has(o.id)
    );
    if (availableIds === null) return []; // still loading
    if (availableIds.size === 0) return activeReferees; // permissive fallback
    return activeReferees.filter((o) => availableIds.has(o.id));
  }, [officials, availableIds, hiddenSet]);
  const isPermissive = availableIds !== null && availableIds.size === 0;
  const isLoadingAvail = availableIds === null;

  // Nettoie les IDs volants qui ne sont plus dans la liste des candidats
  // (ex: décoché suite au filtre "disponibles uniquement").
  useEffect(() => {
    if (availableIds === null) return;
    const validIds = new Set(referees.map((o) => o.id));
    setFloatingIds((prev) => {
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [availableIds, referees]);

  const toggleFloating = (id) => {
    const next = new Set(floatingIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setFloatingIds(next);
  };

  const nameFor = (id) => {
    const o = officials.find((x) => x.id === id);
    return o ? `${o.first_name} ${o.last_name}`.trim() : "—";
  };

  const addForbiddenPair = () => {
    if (!pickA || !pickB || pickA === pickB) {
      toast.error("Choisis deux arbitres différents");
      return;
    }
    const exists = forbiddenPairs.some(
      (p) => (p[0] === pickA && p[1] === pickB) || (p[0] === pickB && p[1] === pickA)
    );
    if (exists) {
      toast.error("Ce duo est déjà interdit");
      return;
    }
    setForbiddenPairs([...forbiddenPairs, [pickA, pickB]]);
    setPickA("");
    setPickB("");
  };

  const removeForbiddenPair = (idx) => {
    setForbiddenPairs(forbiddenPairs.filter((_, i) => i !== idx));
  };

  // Télécharge l'horaire actuel (avec assignations) en JSON pour secours local.
  const downloadCurrent = async () => {
    try {
      const { data } = await api.get(`/events/${eventId}`);
      const payload = { exported_at: new Date().toISOString(), event: data.event };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      const safeName = (eventName || "evenement").replace(/[^a-zA-Z0-9-_]/g, "_").slice(0, 40);
      a.download = `avant_auto_assign_${safeName}_${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Horaire téléchargé — conservé sur ton ordinateur");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur de téléchargement");
    }
  };

  const toggleBlockedCategory = (oid, cat) => {
    setBlockedByOfficial((prev) => {
      const cur = new Set(prev[oid] || []);
      if (cur.has(cat)) cur.delete(cat); else cur.add(cat);
      return { ...prev, [oid]: cur };
    });
  };

  const toggleExcluded = (oid) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(oid)) next.delete(oid); else next.add(oid);
      return next;
    });
  };

  const buildPayload = (isPreview) => ({
    consecutive_max: parseInt(consecutiveMax) || 3,
    max_per_day: maxPerDay ? parseInt(maxPerDay) : null,
    max_break_games: maxBreakGames !== "" && maxBreakGames !== null && maxBreakGames !== undefined ? parseInt(maxBreakGames) : null,
    preserve_manual: preserveManual,
    floating_official_ids: [...floatingIds],
    excluded_official_ids: [...excludedIds],
    forbidden_pairs: forbiddenPairs,
    blocked_categories_by_official: Object.fromEntries(
      Object.entries(blockedByOfficial)
        .map(([oid, set]) => [oid, [...(set || [])]])
        .filter(([, cats]) => cats.length > 0)
    ),
    preferred_block_min: preferredBlockMin !== "" ? parseInt(preferredBlockMin) : null,
    preferred_block_max: preferredBlockMax !== "" ? parseInt(preferredBlockMax) : null,
    preferred_pause_min: preferredPauseMin !== "" ? parseInt(preferredPauseMin) : null,
    isolated_surfaces: [...isolatedSurfaces],
    restricted_surfaces_by_official: Object.fromEntries(
      Object.entries(restrictedByOff)
        .map(([oid, byDate]) => [oid, Object.fromEntries(
          Object.entries(byDate || {})
            .map(([d, set]) => [d, [...(set || [])]])
            .filter(([, arr]) => arr.length > 0)
        )])
        .filter(([, byDate]) => Object.keys(byDate).length > 0)
    ),
    preview: isPreview,
  });

  // Sauvegarde la config (paires interdites + catégories bloquées) sur l'événement.
  // Appelé avant Preview/Apply pour que la config persiste d'un run à l'autre.
  const persistConfig = async () => {
    const payload = {
      forbidden_pairs: forbiddenPairs,
      excluded_official_ids: [...excludedIds],
      blocked_categories_by_official: Object.fromEntries(
        Object.entries(blockedByOfficial)
          .map(([oid, set]) => [oid, [...(set || [])]])
          .filter(([, cats]) => cats.length > 0)
      ),
      isolated_surfaces: [...isolatedSurfaces],
      restricted_surfaces_by_official: Object.fromEntries(
        Object.entries(restrictedByOff)
          .map(([oid, byDate]) => [oid, Object.fromEntries(
            Object.entries(byDate || {})
              .map(([d, set]) => [d, [...(set || [])]])
              .filter(([, arr]) => arr.length > 0)
          )])
          .filter(([, byDate]) => Object.keys(byDate).length > 0)
      ),
    };
    try {
      await api.patch(`/events/${eventId}`, payload);
    } catch {
      // non-bloquant : la préview/apply continue quand même
    }
  };

  const runPreview = async () => {
    setApplying(true);
    // Reset l'aperçu pour que le clic sur Recalculer donne un feedback visuel
    // (sinon on ne voit rien changer quand le résultat est identique).
    setPreview(null);
    try {
      await persistConfig();
      const { data } = await api.post(`/events/${eventId}/auto-assign`, buildPayload(true));
      setPreview(data.report);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setApplying(false);
    }
  };

  const applyNow = async () => {
    if (!preview) return;
    if (!window.confirm(`Appliquer l'auto-assignation ?\n\n${preview.assignments_made} assignations vont être écrites (${preview.uncovered_count} parties resteront sans arbitre). Une sauvegarde automatique sera créée avant.`)) return;
    setApplying(true);
    try {
      await persistConfig();
      const { data } = await api.post(`/events/${eventId}/auto-assign`, buildPayload(false));
      toast.success(`Auto-assignation appliquée · ${data.report.assignments_made} assignations`);
      onApplied && onApplied();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog" aria-modal="true"
      data-testid="auto-assign-modal"
    >
      <div className="rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 relative"
           style={{ background: "var(--surface)", border: "1px solid var(--border-strong)" }}>
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-md hover:bg-[var(--surface-3)]" aria-label="Fermer" data-testid="auto-assign-close-btn">
          <X size={18} strokeWidth={2.2} />
        </button>

        <div className="label mb-1">Assistant IA · assignation</div>
        <h3 className="text-xl font-bold tracking-tight mb-1 flex items-center gap-2">
          <Sparkles size={18} strokeWidth={2.4} />
          Auto-assigner les arbitres
        </h3>
        <p className="text-xs text-[var(--muted)] mb-3">
          « {eventName} » — remplit `referee1_id` et `referee2_id` de chaque partie selon les catégories autorisées, <strong>disponibilités déclarées</strong> et contraintes de fatigue.
        </p>

        {/* Sauvegarde locale (secours) avant lancement */}
        <div
          className="mb-4 rounded-xl p-3 flex items-start gap-3"
          style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
        >
          <Download size={16} strokeWidth={2.4} className="shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-bold mb-0.5">Avant de lancer, tu peux télécharger l&apos;horaire actuel</div>
            <div className="text-[10px] text-[var(--muted)] font-mono">
              Un fichier JSON conservé sur ton ordinateur en cas de problème. Une sauvegarde serveur est aussi créée automatiquement avant l&apos;application.
            </div>
          </div>
          <button
            type="button"
            onClick={downloadCurrent}
            className="btn-ghost inline-flex items-center gap-1.5 text-xs shrink-0"
            data-testid="auto-assign-download-btn"
          >
            <Download size={12} strokeWidth={2.4} /> Télécharger
          </button>
        </div>

        {/* Configuration */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
          <div>
            <label className="label block mb-1">Parties consécutives max</label>
            <input type="number" min="1" value={consecutiveMax} onChange={(e) => setConsecutiveMax(e.target.value)} className="input-box" data-testid="auto-assign-consec-max" />
            <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Sur la même surface</div>
          </div>
          <div>
            <label className="label block mb-1">Max/jour <span className="normal-case font-normal text-[var(--muted)]">(optionnel)</span></label>
            <input type="number" min="1" value={maxPerDay} onChange={(e) => setMaxPerDay(e.target.value)} placeholder="—" className="input-box" data-testid="auto-assign-max-per-day" />
            <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Vide = pas de plafond</div>
          </div>
          <div>
            <label className="label block mb-1">Pause max <span className="normal-case font-normal text-[var(--muted)]">(optionnel)</span></label>
            <input type="number" min="0" value={maxBreakGames} onChange={(e) => setMaxBreakGames(e.target.value)} placeholder="—" className="input-box" data-testid="auto-assign-max-break" />
            <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Parties d&apos;attente max</div>
          </div>
        </div>

        {/* Contraintes HARD : taille de bloc, pauses & restrictions (Feb 2026) */}
        <div className="rounded-xl p-3 mb-4" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
          <div className="label mb-2">Contraintes de bloc <span className="normal-case font-normal text-[var(--muted)]">(dures)</span></div>
          <div className="text-[10px] text-[var(--muted)] mb-3 font-mono leading-relaxed">
            Bloc min/max = plafonds durs. Un arbitre qui ne peut pas enchaîner « bloc min » parties ne démarre PAS sa journée. Laisse vide pour désactiver.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="label block mb-1">Bloc min</label>
              <input type="number" min="1" value={preferredBlockMin} onChange={(e) => setPreferredBlockMin(e.target.value)} placeholder="—" className="input-box" data-testid="auto-assign-block-min" />
              <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Ex : 4</div>
            </div>
            <div>
              <label className="label block mb-1">Bloc max</label>
              <input type="number" min="1" value={preferredBlockMax} onChange={(e) => setPreferredBlockMax(e.target.value)} placeholder="—" className="input-box" data-testid="auto-assign-block-max" />
              <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Ex : 6</div>
            </div>
            <div>
              <label className="label block mb-1">Pause min</label>
              <input type="number" min="1" value={preferredPauseMin} onChange={(e) => setPreferredPauseMin(e.target.value)} placeholder="—" className="input-box" data-testid="auto-assign-pause-min" />
              <div className="text-[10px] text-[var(--muted)] mt-0.5 font-mono">Ex : 2 (préférence)</div>
            </div>
          </div>
          {eventSurfaces > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--border)]">
              <div className="label mb-2">Surfaces éloignées <span className="normal-case font-normal text-[var(--muted)]">(site isolé)</span></div>
              <div className="text-[10px] text-[var(--muted)] mb-2 font-mono leading-relaxed">
                Un arbitre affecté à une surface éloignée y restera de préférence, même après une pause.
              </div>
              <div className="flex flex-wrap gap-1.5" data-testid="auto-assign-isolated-surfaces">
                {Array.from({ length: eventSurfaces }, (_, i) => String(i + 1)).map((s) => {
                  const active = isolatedSurfaces.has(s);
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setIsolatedSurfaces((prev) => {
                        const next = new Set(prev);
                        if (next.has(s)) next.delete(s); else next.add(s);
                        return next;
                      })}
                      className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${active ? "bg-black text-white border-black" : "bg-white text-black border-[var(--border)] hover:border-black"}`}
                      data-testid={`auto-assign-iso-surface-${s}`}
                    >
                      Surface {s}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {eventSurfaces > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--border)]">
              <RestrictionsEditor
                officials={officials}
                hiddenOfficialIds={hiddenOfficialIds}
                games={games}
                surfacesCount={eventSurfaces}
                value={restrictedByOff}
                onChange={setRestrictedByOff}
              />
            </div>
          )}
        </div>

        <label className="flex items-start gap-2 cursor-pointer mb-4 rounded-xl p-3"
               style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
          <input type="checkbox" checked={preserveManual} onChange={(e) => setPreserveManual(e.target.checked)} className="mt-0.5 w-3.5 h-3.5" data-testid="auto-assign-preserve" />
          <span className="text-sm leading-snug">
            <span className="font-semibold">Préserver les assignations manuelles</span>
            <span className="block text-xs text-[var(--muted)] mt-0.5">Ne remplit que les slots vides — les arbitres déjà assignés manuellement sont conservés.</span>
          </span>
        </label>

        {/* Volants */}
        <div className="mb-4">
          <div className="label mb-2 flex items-center gap-2">
            <Users size={12} strokeWidth={2.4} /> Arbitres volants pour cet événement
          </div>
          <div className="text-[10px] text-[var(--muted)] mb-2 font-mono">
            Les volants peuvent dépasser le max consécutif pour couvrir les pauses des autres.
            {isPermissive && (
              <span className="block mt-1">
                Note : aucune disponibilité déclarée — tous les arbitres actifs sont proposés.
              </span>
            )}
            {!isPermissive && !isLoadingAvail && (
              <span className="block mt-1">Filtré aux arbitres ayant déclaré au moins un créneau disponible.</span>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto rounded-xl p-2"
               style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
            {isLoadingAvail && (
              <div className="col-span-full text-xs text-[var(--muted)] py-2 px-1">Chargement des disponibilités…</div>
            )}
            {!isLoadingAvail && referees.length === 0 && (
              <div className="col-span-full text-xs text-[var(--muted)] py-2 px-1" data-testid="auto-assign-floating-empty">
                Aucun arbitre disponible pour cet événement
              </div>
            )}
            {referees.map((o) => (
              <label key={o.id} className="flex items-center gap-1.5 text-xs cursor-pointer py-1 px-1 hover:bg-white/50 rounded"
                     data-testid={`auto-assign-floating-${o.id}`}>
                <input type="checkbox" checked={floatingIds.has(o.id)} onChange={() => toggleFloating(o.id)} className="w-3.5 h-3.5" />
                <span className="truncate">{o.first_name} {o.last_name}</span>
              </label>
            ))}
          </div>
        </div>

        {/* Retirer manuellement des arbitres */}
        <div className="mb-4">
          <div className="label mb-2 flex items-center gap-2">
            <Ban size={12} strokeWidth={2.4} /> Retirer des arbitres de l&apos;auto-assignation
          </div>
          <div className="text-[10px] text-[var(--muted)] mb-2 font-mono">
            Coche un arbitre pour l&apos;écarter de <strong>cette</strong> auto-assignation, même s&apos;il s&apos;est déclaré disponible. Config persistée sur l&apos;événement.
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto rounded-xl p-2"
               style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
            {isLoadingAvail && (
              <div className="col-span-full text-xs text-[var(--muted)] py-2 px-1">Chargement…</div>
            )}
            {!isLoadingAvail && referees.length === 0 && (
              <div className="col-span-full text-xs text-[var(--muted)] py-2 px-1">Aucun arbitre à retirer</div>
            )}
            {referees.map((o) => (
              <label key={o.id} className="flex items-center gap-1.5 text-xs cursor-pointer py-1 px-1 hover:bg-white/50 rounded"
                     data-testid={`auto-assign-exclude-${o.id}`}>
                <input
                  type="checkbox"
                  checked={excludedIds.has(o.id)}
                  onChange={() => toggleExcluded(o.id)}
                  className="w-3.5 h-3.5"
                />
                <span className={`truncate ${excludedIds.has(o.id) ? "line-through text-[var(--muted)]" : ""}`}>
                  {o.first_name} {o.last_name}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Duos interdits */}
        <div className="mb-4">
          <div className="label mb-2 flex items-center gap-2">
            <Ban size={12} strokeWidth={2.4} /> Duos d&apos;arbitres à ne pas jumeler
          </div>
          <div className="text-[10px] text-[var(--muted)] mb-2 font-mono">
            Aucune paire cochée ci-dessous ne sera assignée ensemble sur une même partie.
          </div>
          <div className="flex flex-wrap items-end gap-2 mb-2">
            <div className="flex-1 min-w-[8rem]">
              <label className="label block mb-1">Arbitre A</label>
              <select value={pickA} onChange={(e) => setPickA(e.target.value)} className="input-box" data-testid="auto-assign-pair-a">
                <option value="">—</option>
                {referees.map((o) => (
                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[8rem]">
              <label className="label block mb-1">Arbitre B</label>
              <select value={pickB} onChange={(e) => setPickB(e.target.value)} className="input-box" data-testid="auto-assign-pair-b">
                <option value="">—</option>
                {referees.filter((o) => o.id !== pickA).map((o) => (
                  <option key={o.id} value={o.id}>{o.first_name} {o.last_name}</option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={addForbiddenPair}
              disabled={!pickA || !pickB}
              className="btn-ghost inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="auto-assign-add-pair"
            >
              <Plus size={12} strokeWidth={2.5} /> Ajouter
            </button>
          </div>
          {forbiddenPairs.length > 0 && (
            <ul className="space-y-1" data-testid="auto-assign-forbidden-list">
              {forbiddenPairs.map(([a, b], i) => (
                <li
                  key={`${a}__${b}`}
                  className="flex items-center justify-between text-xs px-2 py-1 rounded"
                  style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}
                  data-testid={`auto-assign-forbidden-${i}`}
                >
                  <span>
                    <strong>{nameFor(a)}</strong>
                    <span className="mx-2 text-[var(--muted)]">×</span>
                    <strong>{nameFor(b)}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => removeForbiddenPair(i)}
                    className="text-[var(--muted)] hover:text-black p-0.5"
                    aria-label="Retirer ce duo"
                    data-testid={`auto-assign-remove-pair-${i}`}
                  >
                    <X size={12} strokeWidth={2.5} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Catégories bloquées par arbitre (par événement) */}
        <div className="mb-4">
          <div className="label mb-2 flex items-center gap-2">
            <Ban size={12} strokeWidth={2.4} /> Catégories bloquées par arbitre
          </div>
          <div className="text-[10px] text-[var(--muted)] mb-2 font-mono">
            Coche une catégorie pour l&apos;interdire à cet arbitre <strong>uniquement pour ce tournoi</strong>. La liste des catégories est extraite automatiquement de l&apos;horaire.
          </div>
          {eventCategories.length === 0 ? (
            <div className="text-xs text-[var(--muted)] py-2 px-2 rounded" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              Aucune catégorie détectée dans l&apos;horaire (renseigne la colonne « Catégorie » dans tes parties).
            </div>
          ) : referees.length === 0 ? (
            <div className="text-xs text-[var(--muted)] py-2 px-2 rounded" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              Aucun arbitre disponible pour cet événement.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl" style={{ background: "var(--surface-3)", border: "1px solid var(--border)" }}>
              <table className="w-full text-xs" data-testid="auto-assign-blocked-cats-table">
                <thead>
                  <tr className="border-b border-[var(--border)]">
                    <th className="text-left py-1.5 px-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Arbitre</th>
                    {eventCategories.map((cat) => (
                      <th key={cat} className="py-1.5 px-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]" data-testid={`blocked-cat-header-${cat}`}>
                        {cat}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referees.map((o) => (
                    <tr key={o.id} className="border-b border-[var(--border)] last:border-b-0">
                      <td className="py-1 px-2 whitespace-nowrap">{o.first_name} {o.last_name}</td>
                      {eventCategories.map((cat) => {
                        const set = blockedByOfficial[o.id];
                        const blocked = set instanceof Set ? set.has(cat) : false;
                        return (
                          <td key={cat} className="py-1 px-2 text-center">
                            <input
                              type="checkbox"
                              checked={blocked}
                              onChange={() => toggleBlockedCategory(o.id, cat)}
                              className="w-3.5 h-3.5 cursor-pointer"
                              data-testid={`blocked-cat-${o.id}-${cat}`}
                              title={blocked ? `Débloquer ${cat} pour ${o.first_name} ${o.last_name}` : `Bloquer ${cat} pour ${o.first_name} ${o.last_name}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Preview report */}
        {preview && (
          <div className="mb-4 rounded-xl p-4"
               style={{ background: "var(--surface-3)", border: "1px solid var(--border-strong)" }}
               data-testid="auto-assign-preview">
            <div className="label mb-2">Aperçu</div>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <Stat label="Assignations" value={preview.assignments_made} />
              <Stat label="Slots totaux" value={preview.total_slots} />
              <Stat label="Non couverts" value={preview.uncovered_count} highlight={preview.uncovered_count > 0} />
            </div>
            {preview.uncovered_count > 0 && (
              <div className="text-xs mb-2 p-2 rounded" style={{ background: "#dbeafe" }}>
                <AlertTriangle size={11} strokeWidth={2.4} className="inline mr-1" />
                <strong>{preview.uncovered_count}</strong> slot(s) resteront vides — vérifie tes catégories autorisées, tes disponibilités ou ton nombre d&apos;arbitres.
              </div>
            )}

            {/* Checklist des critères respectés */}
            {preview.checks && preview.checks.length > 0 && (
              <div className="mb-3 rounded-lg p-3" style={{ background: "var(--surface)", border: "1px solid var(--border)" }} data-testid="auto-assign-checks">
                <div className="label mb-2">Critères vérifiés</div>
                <ul className="space-y-1">
                  {preview.checks.map((c) => (
                    <li key={c.id} className="flex items-start gap-2 text-xs" data-testid={`check-${c.id}`}>
                      <span className="shrink-0 mt-0.5">
                        {c.status === "ok" && <Check size={12} strokeWidth={2.6} className="text-black" />}
                        {c.status === "warn" && <TriangleAlert size={12} strokeWidth={2.4} className="text-black" />}
                        {c.status === "skip" && <Minus size={12} strokeWidth={2.4} className="text-[var(--muted)]" />}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className={`font-semibold ${c.status === "skip" ? "text-[var(--muted)]" : ""}`}>{c.label}</span>
                        <span className="text-[var(--muted)] ml-2">— {c.detail}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {preview.fairness && preview.per_official && preview.per_official.length > 0 && (
              <div className="text-xs mb-2 p-2 rounded flex items-center flex-wrap gap-x-3 gap-y-1"
                   style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
                   data-testid="auto-assign-fairness">
                <span className="font-semibold">Fairness :</span>
                <span>Moyenne <strong className="tabular">{preview.fairness.mean_games}</strong> parties/arbitre</span>
                <span>·</span>
                <span>Écart-type <strong className="tabular">{preview.fairness.stddev}</strong></span>
                <span>·</span>
                <span>Min <strong className="tabular">{preview.fairness.min_games}</strong> / Max <strong className="tabular">{preview.fairness.max_games}</strong></span>
                <span className={`ml-auto text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 border ${
                  preview.fairness.label === "excellent" ? "bg-black text-white border-black" :
                  preview.fairness.label === "bon" ? "border-black" :
                  "border-black"
                }`}
                style={preview.fairness.label === "moyen" || preview.fairness.label === "faible" ? { background: "#dbeafe" } : {}}
                data-testid="auto-assign-fairness-badge">
                  {preview.fairness.label}
                </span>
              </div>
            )}
            <div className="text-[10px] text-[var(--muted)] font-mono mb-2" data-testid="auto-assign-availability-notice">
              {preview.strict_availability_mode
                ? "✓ Mode strict — seuls les arbitres ayant répondu OUI à la demande de dispos sont assignés"
                : preview.availability_respected
                ? "✓ Disponibilités déclarées respectées"
                : "⚠ Aucune demande de dispos envoyée pour cet événement — algorithme en mode permissif (tous les arbitres considérés disponibles)"}
            </div>
            {preview.uncovered_count > 0 && preview.uncovered_details && preview.uncovered_details.length > 0 && (
              <details open className="text-xs mt-2 border-2 border-black bg-white" data-testid="auto-assign-uncovered">
                <summary className="cursor-pointer font-semibold py-2 px-2 uppercase tracking-wider text-[11px]">
                  ⚠ {preview.uncovered_count} slot(s) impossible(s) à couvrir — voir contraintes non satisfaites
                </summary>
                {(() => {
                  const reasonSet = new Set();
                  (preview.uncovered_details || []).forEach((u) => (u.reasons || []).forEach((r) => reasonSet.add(r)));
                  const suggestions = [];
                  if (reasonSet.has("forbidden_pair") && forbiddenPairs.length > 0) {
                    suggestions.push({
                      key: "forbidden_pair",
                      label: `Retirer tous les duos interdits (${forbiddenPairs.length})`,
                      action: () => { setForbiddenPairs([]); toast.info("Duos interdits retirés — clique Recalculer"); },
                    });
                  }
                  if (reasonSet.has("blocked_category")) {
                    const blockedCount = Object.values(blockedByOfficial).reduce((n, s) => n + (s?.size || 0), 0);
                    if (blockedCount > 0) {
                      suggestions.push({
                        key: "blocked_category",
                        label: `Retirer toutes les catégories bloquées (${blockedCount})`,
                        action: () => { setBlockedByOfficial({}); toast.info("Catégories bloquées retirées — clique Recalculer"); },
                      });
                    }
                  }
                  if (reasonSet.has("break_too_long") && maxBreakGames !== "") {
                    const next = (parseInt(maxBreakGames) || 0) + 1;
                    suggestions.push({
                      key: "break_too_long",
                      label: `Augmenter Max de pause à ${next}`,
                      action: () => { setMaxBreakGames(String(next)); toast.info(`Max de pause → ${next} — clique Recalculer`); },
                    });
                  }
                  if (reasonSet.has("consecutive_max")) {
                    const next = (parseInt(consecutiveMax) || 3) + 1;
                    suggestions.push({
                      key: "consecutive_max",
                      label: `Augmenter Max consécutif à ${next}`,
                      action: () => { setConsecutiveMax(next); toast.info(`Max consécutif → ${next} — clique Recalculer`); },
                    });
                  }
                  if (reasonSet.has("max_day") && maxPerDay !== "") {
                    const next = (parseInt(maxPerDay) || 0) + 1;
                    suggestions.push({
                      key: "max_day",
                      label: `Augmenter Max/jour à ${next}`,
                      action: () => { setMaxPerDay(String(next)); toast.info(`Max/jour → ${next} — clique Recalculer`); },
                    });
                  }
                  if (reasonSet.has("surface_change")) {
                    suggestions.push({
                      key: "surface_change",
                      label: "Ajouter un volant (arbitre non capé) pour absorber ce trou",
                      action: null, // structural
                    });
                  }
                  return suggestions.length > 0 ? (
                    <div className="px-2 pt-2 pb-1 border-b border-[var(--border)] bg-[var(--surface-3)]" data-testid="auto-assign-suggestions">
                      <div className="text-[10px] uppercase tracking-wider font-mono mb-1">Correctifs suggérés</div>
                      <div className="flex flex-wrap gap-1.5">
                        {suggestions.map((s) => (
                          <button
                            key={s.key}
                            type="button"
                            onClick={s.action || undefined}
                            disabled={!s.action}
                            className={`text-[11px] px-2 py-1 border border-black ${s.action ? "hover:bg-black hover:text-white" : "opacity-60 cursor-not-allowed"}`}
                            data-testid={`auto-assign-fix-${s.key}`}
                            title={s.action ? "Ajuste le paramètre puis clique Recalculer" : "Correctif structurel — pas d'action automatique"}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null;
                })()}
                <ul className="p-2 space-y-1 max-h-56 overflow-y-auto">
                  {preview.uncovered_details.map((u, i) => {
                    const labels = {
                      category: "catégorie non autorisée",
                      blocked_category: "catégorie bloquée pour l'arbitre",
                      availability: "aucun arbitre disponible ce créneau",
                      collision: "collisions horaires",
                      max_day: "max/jour atteint pour tous",
                      forbidden_pair: "duos interdits bloquants",
                      break_too_long: "max de pause dépassé",
                      consecutive_max: "max consécutif atteint",
                      surface_change: "changement de surface interdit en streak",
                      no_candidate: "aucun candidat éligible",
                    };
                    return (
                      <li key={`${u.game_id}-${u.slot}-${i}`} className="border-b border-[var(--border)] py-1 last:border-0">
                        <div className="font-mono text-[10px]">
                          {u.date} · {u.start_time} · Surface {u.surface} · {u.category || "—"} · <b>{u.slot === "referee1_id" ? "Arbitre 1" : "Arbitre 2"}</b>
                        </div>
                        <div className="text-[11px] text-[var(--muted)]">
                          Contrainte(s) empêchant l&apos;assignation : {u.reasons.map((r) => labels[r] || r).join(" · ")}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </details>
            )}
            {preview.excluded_officials && preview.excluded_officials.length > 0 && (
              <details className="text-xs mt-2" data-testid="auto-assign-excluded">
                <summary className="cursor-pointer font-semibold py-1">
                  Officiels non assignés ({preview.excluded_officials.length}) — voir raisons
                </summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto rounded p-2"
                    style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
                  {preview.excluded_officials.map((e) => (
                    <li key={e.official_id} className="flex justify-between items-start gap-2 border-b border-[var(--border)] py-1 last:border-0" data-testid={`auto-assign-excluded-${e.official_id}`}>
                      <div className="min-w-0">
                        <div className="font-medium truncate">{e.name}</div>
                        <div className="text-[10px] text-[var(--muted)] font-mono">{e.primary_reason}</div>
                      </div>
                      <div className="text-[10px] text-[var(--muted)] tabular whitespace-nowrap shrink-0">
                        {e.counts.category > 0 && <span title="Catégorie non autorisée">Cat: {e.counts.category}</span>}
                        {e.counts.blocked_category > 0 && <span title="Catégorie bloquée (config événement)" className="ml-2">Bloqué: {e.counts.blocked_category}</span>}
                        {e.counts.availability > 0 && <span title="Non disponible" className="ml-2">Dispo: {e.counts.availability}</span>}
                        {e.counts.collision > 0 && <span title="Conflit horaire" className="ml-2">Conflit: {e.counts.collision}</span>}
                        {e.counts.max_day > 0 && <span title="Max/jour atteint" className="ml-2">Max: {e.counts.max_day}</span>}
                        {e.counts.forbidden_pair > 0 && <span title="Duo interdit" className="ml-2">Duo: {e.counts.forbidden_pair}</span>}
                        {e.counts.break_too_long > 0 && <span title="Trop de parties de pause" className="ml-2">Pause: {e.counts.break_too_long}</span>}
                        {e.counts.consecutive_max > 0 && <span title="Max consécutif atteint" className="ml-2">Consec: {e.counts.consecutive_max}</span>}
                        {e.counts.surface_change > 0 && <span title="Changement de surface interdit en streak" className="ml-2">Surface: {e.counts.surface_change}</span>}
                      </div>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {preview.per_official && preview.per_official.length > 0 && (
              <details className="text-xs" data-testid="auto-assign-per-official">
                <summary className="cursor-pointer font-semibold py-1">Charge par arbitre ({preview.per_official.length})</summary>
                <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                  {preview.per_official.map((r) => (
                    <li key={r.official_id} className="flex justify-between border-b border-[var(--border)] py-1">
                      <span>{r.name}{r.is_floating && <span className="ml-1 text-[9px] uppercase bg-black text-white px-1">volant</span>}</span>
                      <span className="tabular font-semibold">{r.games} partie{r.games > 1 ? "s" : ""}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
            {preview.playable_schedule && preview.playable_schedule.length > 0 && preview.timeline_by_official && preview.timeline_by_official.length > 0 && (
              <details className="text-xs mt-2" data-testid="auto-assign-timeline">
                <summary className="cursor-pointer font-semibold py-1">Frise horaire par arbitre — repérer les streaks & pauses</summary>
                <div className="mt-2 overflow-x-auto border border-black">
                  <table className="text-[10px] tabular border-collapse">
                    <thead>
                      <tr className="border-b border-black bg-[var(--surface-3)]">
                        <th className="sticky left-0 bg-[var(--surface-3)] px-2 py-1 text-left font-mono uppercase tracking-wider z-10 border-r border-black">Arbitre</th>
                        {preview.playable_schedule.map((s) => (
                          <th key={s.idx} className="px-1 py-1 border-r border-[var(--border)] text-center font-mono font-normal" title={`${s.date} ${s.start_time} · Surface ${s.surface} · ${s.category || "—"}`}>
                            <div className="whitespace-nowrap">S{s.surface}</div>
                            <div className="text-[9px] text-[var(--muted)]">{s.start_time}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.timeline_by_official.map((row) => {
                        const off = officials.find((o) => o.id === row.official_id);
                        const name = off ? `${off.first_name} ${off.last_name}` : row.official_id;
                        const playedSet = new Set(row.played_indices);
                        const firstIdx = row.played_indices[0];
                        const lastIdx = row.played_indices[row.played_indices.length - 1];
                        return (
                          <tr key={row.official_id} className="border-b border-[var(--border)]" data-testid={`timeline-row-${row.official_id}`}>
                            <td className="sticky left-0 bg-white px-2 py-1 whitespace-nowrap border-r border-black">{name}</td>
                            {preview.playable_schedule.map((s) => {
                              const isPlayed = playedSet.has(s.idx);
                              const inRange = firstIdx !== undefined && s.idx >= firstIdx && s.idx <= lastIdx;
                              const isPause = !isPlayed && inRange;
                              const bg = isPlayed ? "black" : isPause ? "repeating-linear-gradient(45deg, #ddd, #ddd 3px, #fff 3px, #fff 6px)" : "transparent";
                              return (
                                <td key={s.idx} className="border-r border-[var(--border)]">
                                  <div style={{ background: bg, width: 20, height: 16 }} title={`${isPlayed ? "Joué" : isPause ? "Pause" : "—"} · ${s.date} ${s.start_time} · Surface ${s.surface}`} />
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center gap-3 mt-1 text-[10px] font-mono text-[var(--muted)]">
                  <span className="inline-flex items-center gap-1"><span style={{ background: "black", width: 12, height: 10, display: "inline-block" }} /> Joué</span>
                  <span className="inline-flex items-center gap-1"><span style={{ background: "repeating-linear-gradient(45deg, #ddd, #ddd 3px, #fff 3px, #fff 6px)", width: 12, height: 10, display: "inline-block", border: "1px solid var(--border)" }} /> Pause entre 2 assignations</span>
                </div>
              </details>
            )}
          </div>
        )}

        <div className="flex justify-between gap-2">
          <button onClick={onClose} className="btn-ghost" data-testid="auto-assign-cancel-btn">
            Annuler
          </button>
          <div className="flex gap-2">
            {!preview && (
              <button onClick={runPreview} disabled={applying} className="btn-primary inline-flex items-center gap-1.5" data-testid="auto-assign-preview-btn">
                <Sparkles size={14} strokeWidth={2.4} /> Prévisualiser
              </button>
            )}
            {preview && (
              <>
                <button onClick={runPreview} disabled={applying} className="btn-ghost" data-testid="auto-assign-repreview-btn">
                  Recalculer
                </button>
                <button onClick={applyNow} disabled={applying} className="btn-primary" data-testid="auto-assign-apply-btn">
                  Appliquer ({preview.assignments_made})
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="rounded-lg p-2 text-center"
         style={{ background: highlight ? "#dbeafe" : "var(--surface)", border: "1px solid var(--border)" }}>
      <div className="text-lg font-bold tabular">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-[var(--muted)]">{label}</div>
    </div>
  );
}
